/**
 * Raising an exception, and editing a draft.
 *
 * The policy is chosen from the published ones, and the version reference
 * the backend wants (id and number) is read from the policy document at
 * that moment — an exception is against a specific version, so that a
 * later change to the policy can be flagged underneath it.
 */

import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useTaxonomy } from '../admin/useTaxonomy';
import { usePolicies } from '../policies/usePolicies';
import { isCallFailure } from '../../lib/callable';
import {
  EXC_ASSESSMENT_MAX, EXC_COMMITTEE_MAX, EXC_CONTROL_MAX, EXC_CONTROLS_MAX, EXC_RENEWALS_MAX, EXC_REVIEW_DAYS_MAX,
  EXC_TEXT_MAX, EXC_TITLE_MAX,
} from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { ListInput } from '../../shared/ListInput';
import { SegmentedChoice } from '../../shared/SegmentedChoice';
import { toDateInput } from '../../shared/dates';
import { useCreateException, useExceptionDetail, usePolicyVersionRef, useUpdateException } from './useExceptions';
import {
  EMPTY_FIELDS, EXCEPTION_TYPES, REASON_CATEGORIES, REASON_LABEL, RISK_LABEL, RISK_LEVELS, TYPE_LABEL,
  type ExceptionFields, type ExceptionRecord,
} from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Exceptions.module.css';

export const NOT_DRAFT_TITLE = 'Only a draft can be edited';

const TYPE_HINT: Record<(typeof EXCEPTION_TYPES)[number], string> = {
  exception: 'A one-off departure from the policy, for a reason and a time.',
  variance: 'A standing difference in how a unit applies the policy.',
  waiver: 'A requirement of the policy set aside altogether.',
};

function fieldsFrom(e: ExceptionRecord): ExceptionFields {
  return {
    title: e.title, exceptionType: e.exceptionType, riskLevel: e.riskLevel,
    policyId: e.policyId, policyVersionId: e.policyVersionId, policyVersionNumber: e.policyVersionNumber || null,
    policyTitle: e.policyTitle, description: e.description, justification: e.justification,
    mitigatingControls: e.mitigatingControls, impactAssessment: e.impactAssessment ?? '',
    conditions: e.conditions ?? '', affectedDepartmentIds: e.affectedDepartmentIds,
    expirationDate: toDateInput(e.expirationDate),
    reviewIntervalDays: e.reviewIntervalDays ? String(e.reviewIntervalDays) : '',
    maxRenewals: e.maxRenewals !== null ? String(e.maxRenewals) : '',
    reasonCategory: e.reasonCategory, oversightCommittee: e.oversightCommittee ?? '',
  };
}

export function ExceptionEditPage() {
  const { exceptionId } = useParams();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const editing = Boolean(exceptionId);
  const detail = useExceptionDetail(orgId, exceptionId ?? null, editing);

  if (!editing) return <ExceptionForm orgId={orgId} />;
  if (detail.isLoading) return <Spinner label="Opening the exception" />;
  if (detail.error || !detail.data) {
    return (
      <div className={styles.body}>
        <PageState title="Could not open the exception" action={{ to: '/exceptions', label: 'Back to exceptions' }} tone="alert">
          {isCallFailure(detail.error) ? detail.error.message : 'Something went wrong.'}
        </PageState>
      </div>
    );
  }
  if (detail.data.exception.status !== 'draft') {
    return (
      <div className={styles.body}>
        <PageState title={NOT_DRAFT_TITLE} action={{ to: `/exceptions/${exceptionId}`, label: 'Back to the exception' }}>
          Once submitted, an exception is changed through its approvals, reviews and renewals, not by editing.
          Recall a pending submission to make it a draft again.
        </PageState>
      </div>
    );
  }
  return <ExceptionForm orgId={orgId} record={detail.data.exception} />;
}

function ExceptionForm({ orgId, record }: { orgId: string | null; record?: ExceptionRecord }) {
  const navigate = useNavigate();
  const { session } = useAuth();
  const roles = session?.roles ?? [];
  const create = useCreateException(orgId);
  const update = useUpdateException(orgId, record?.id ?? '');
  const departments = useTaxonomy('department', Boolean(orgId));
  const policies = usePolicies(orgId, roles, 'published');
  const [f, setF] = useState<ExceptionFields>(() => (record ? fieldsFrom(record) : EMPTY_FIELDS));
  const [touched, setTouched] = useState(false);
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;

  // The version reference follows the chosen policy.
  const ref = usePolicyVersionRef(orgId, f.policyId);
  // Applied while rendering, once per answer, rather than in an effect.
  const [refApplied, setRefApplied] = useState<unknown>(null);
  if (ref.data && f.policyId && ref.data !== refApplied) {
    const r = ref.data;
    setRefApplied(r);
    setF((c) => ({
      ...c,
      policyVersionId: r.versionId ?? '',
      policyVersionNumber: r.versionNumber,
      policyTitle: c.policyTitle || r.title,
    }));
  }

  const set = <K extends keyof ExceptionFields>(k: K, v: ExceptionFields[K]) => setF((c) => ({ ...c, [k]: v }));
  const text = (k: keyof ExceptionFields, max: number) => (e: { target: { value: string } }) => set(k, e.target.value.slice(0, max) as never);

  const missing = {
    title: !f.title.trim(),
    policy: !f.policyId,
    version: Boolean(f.policyId) && !ref.isLoading && !f.policyVersionId,
    description: !f.description.trim(),
    justification: !f.justification.trim(),
    controls: f.mitigatingControls.length === 0,
    departments: f.affectedDepartmentIds.length === 0,
    expiration: !f.expirationDate,
  };
  const anyMissing = Object.values(missing).some(Boolean);
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const backTo = record ? `/exceptions/${record.id}` : '/exceptions';
  const show = (k: keyof typeof missing) => touched && missing[k];

  function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (anyMissing) return;
    if (record) update.mutate(f, { onSuccess: () => navigate(backTo) });
    else create.mutate(f, { onSuccess: (newId) => navigate(`/exceptions/${newId}`) });
  }

  function toggleDept(did: string) {
    set('affectedDepartmentIds', f.affectedDepartmentIds.includes(did)
      ? f.affectedDepartmentIds.filter((d) => d !== did)
      : [...f.affectedDepartmentIds, did]);
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          {record && <p className={styles.eyebrow}>{TYPE_LABEL[record.exceptionType]} · Draft</p>}
          <div className={styles.titleLine}>
            <BackLink to={backTo} label={record ? 'Back to the exception' : 'Back to exceptions'} />
            <h1 className={styles.title}>{record ? `Edit ${record.title}` : 'New exception'}</h1>
          </div>
          <p className={styles.subtitle}>
            {record ? 'Changes are saved to this draft. Submit it for approval when it is ready.'
              : 'Say what will be done differently, against which policy, why, and what keeps it safe. It starts as a draft.'}
          </p>
        </div>
      </header>

      <div className={styles.body}>
        <form className={form.form} onSubmit={submit} noValidate style={{ marginTop: 0, maxWidth: 760 }}>
          {error && (
            <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
              {isCallFailure(error) ? error.message : 'The exception could not be saved.'}
            </div>
          )}

          <div className={form.field}>
            <label className={form.label} htmlFor={id('title')}>Title</label>
            <input id={id('title')} className={form.input} type="text" required maxLength={EXC_TITLE_MAX}
              value={f.title} onChange={text('title', EXC_TITLE_MAX)} aria-invalid={show('title') || undefined}
              aria-describedby={show('title') ? id('title-err') : undefined} autoComplete="off" />
            <CharCount value={f.title} max={EXC_TITLE_MAX} />
            {show('title') && <p className={form.error} id={id('title-err')}>Give the exception a title.</p>}
          </div>

          <div className={form.row}>
            <SegmentedChoice legend="Type" name={id('type')} value={f.exceptionType} onChange={(v) => set('exceptionType', v)} stacked
              options={EXCEPTION_TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t], description: TYPE_HINT[t] }))} />
            <SegmentedChoice legend="Risk level" name={id('risk')} value={f.riskLevel} onChange={(v) => set('riskLevel', v)}
              options={RISK_LEVELS.map((r) => ({ value: r, label: RISK_LABEL[r] }))}
              describedBy={id('risk-hint')} />
          </div>
          <p className={form.hint} id={id('risk-hint')} style={{ marginTop: 'calc(var(--sp-4) * -1)', marginBottom: 'var(--sp-6)' }}>
            The risk level decides who must approve: high and critical need two approvers including an administrator.
          </p>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('policy')}>Policy</label>
            <select id={id('policy')} className={form.select} required value={f.policyId}
              onChange={(e) => {
                const p = (policies.data ?? []).find((x) => x.id === e.target.value);
                setF((c) => ({ ...c, policyId: e.target.value, policyTitle: p?.title ?? '', policyVersionId: '', policyVersionNumber: null }));
              }}
              aria-invalid={show('policy') || missing.version || undefined}
              aria-describedby={id('policy-hint')} disabled={Boolean(record)}>
              <option value="">{policies.isLoading ? 'Loading published policies…' : 'Choose a published policy'}</option>
              {(policies.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.policyNumber ? `${p.policyNumber} — ` : ''}{p.title}</option>
              ))}
              {record && !(policies.data ?? []).some((p) => p.id === record.policyId) && (
                <option value={record.policyId}>{record.policyTitle}</option>
              )}
            </select>
            <p className={missing.version ? form.error : form.hint} id={id('policy-hint')}>
              {show('policy') ? 'Choose the policy this departs from.'
                : missing.version ? 'That policy has no published version; an exception is raised against a published one.'
                : f.policyVersionNumber ? `Against version ${f.policyVersionNumber}. A later change to the policy will be flagged on this exception.`
                : record ? 'The policy cannot change on an existing exception.' : 'Only published policies are offered.'}
            </p>
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('desc')}>Description</label>
            <textarea id={id('desc')} className={form.textarea} rows={4} required maxLength={EXC_TEXT_MAX}
              value={f.description} onChange={text('description', EXC_TEXT_MAX)} aria-invalid={show('description') || undefined}
              aria-describedby={id('desc-hint')} />
            <CharCount value={f.description} max={EXC_TEXT_MAX} />
            <p className={show('description') ? form.error : form.hint} id={id('desc-hint')}>
              {show('description') ? 'Describe what will be done differently.' : 'What will be done differently from the policy, and where.'}
            </p>
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('just')}>Justification</label>
            <textarea id={id('just')} className={form.textarea} rows={4} required maxLength={EXC_TEXT_MAX}
              value={f.justification} onChange={text('justification', EXC_TEXT_MAX)} aria-invalid={show('justification') || undefined}
              aria-describedby={id('just-hint')} />
            <CharCount value={f.justification} max={EXC_TEXT_MAX} />
            <p className={show('justification') ? form.error : form.hint} id={id('just-hint')}>
              {show('justification') ? 'Say why the exception is needed.' : 'Why the policy cannot be followed as written here — the case a surveyor will read.'}
            </p>
          </div>

          <div className={form.row}>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('reason')}>Reason</label>
              <select id={id('reason')} className={form.select} value={f.reasonCategory}
                onChange={(e) => set('reasonCategory', e.target.value as ExceptionFields['reasonCategory'])} aria-describedby={id('reason-hint')}>
                {REASON_CATEGORIES.map((r) => <option key={r} value={r}>{REASON_LABEL[r]}</option>)}
              </select>
              <p className={form.hint} id={id('reason-hint')}>The category the quality council reports on.</p>
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('committee')}>Oversight committee <span className={form.optional}>(optional)</span></label>
              <input id={id('committee')} className={form.input} type="text" maxLength={EXC_COMMITTEE_MAX}
                value={f.oversightCommittee} onChange={text('oversightCommittee', EXC_COMMITTEE_MAX)} autoComplete="off" />
              <CharCount value={f.oversightCommittee} max={EXC_COMMITTEE_MAX} />
            </div>
          </div>

          <div className={form.field}>
            <span className={form.label} id={id('ctrl-label')}>Mitigating controls</span>
            <ListInput id={id('ctrl')} labelledBy={id('ctrl-label')} describedBy={id('ctrl-hint')}
              value={f.mitigatingControls} onChange={(v) => set('mitigatingControls', v)}
              max={EXC_CONTROLS_MAX} itemMax={EXC_CONTROL_MAX} placeholder="One control per entry — press Enter after each" />
            <p className={show('controls') ? form.error : form.hint} id={id('ctrl-hint')}>
              {show('controls') ? 'At least one control is required.' : 'What keeps patients safe while the policy is not followed as written.'}
            </p>
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('impact')}>Impact assessment <span className={form.optional}>(optional)</span></label>
            <textarea id={id('impact')} className={form.textarea} rows={3} maxLength={EXC_ASSESSMENT_MAX}
              value={f.impactAssessment} onChange={text('impactAssessment', EXC_ASSESSMENT_MAX)} />
            <CharCount value={f.impactAssessment} max={EXC_ASSESSMENT_MAX} />
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('cond')}>Conditions to maintain <span className={form.optional}>(optional)</span></label>
            <textarea id={id('cond')} className={form.textarea} rows={3} maxLength={EXC_ASSESSMENT_MAX}
              value={f.conditions} onChange={text('conditions', EXC_ASSESSMENT_MAX)} aria-describedby={id('cond-hint')} />
            <CharCount value={f.conditions} max={EXC_ASSESSMENT_MAX} />
            <p className={form.hint} id={id('cond-hint')}>What must stay true while the exception is active.</p>
          </div>

          <fieldset className={form.field} style={{ border: 0, padding: 0, margin: '0 0 var(--sp-6)' }}
            aria-describedby={id('dept-hint')} aria-invalid={show('departments') || undefined}>
            <legend className={form.label}>Affected departments</legend>
            {(departments.data ?? []).length === 0 ? (
              <p className={form.hint}>No departments are set up yet. An administrator adds them under Taxonomy.</p>
            ) : (
              <ul className={own.checks}>
                {(departments.data ?? []).filter((d) => d.isActive || f.affectedDepartmentIds.includes(d.id)).map((d) => (
                  <li key={d.id}>
                    <label className={own.check}>
                      <input type="checkbox" checked={f.affectedDepartmentIds.includes(d.id)} onChange={() => toggleDept(d.id)} />
                      {d.name}
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <p className={show('departments') ? form.error : form.hint} id={id('dept-hint')}>
              {show('departments') ? 'Choose at least one department.' : 'Where the exception applies.'}
            </p>
          </fieldset>

          <h2 className={form.group}>Duration and review</h2>
          <div className={form.row}>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('exp')}>Expiration date</label>
              <input id={id('exp')} className={form.input} type="date" required value={f.expirationDate}
                onChange={(e) => set('expirationDate', e.target.value)} aria-invalid={show('expiration') || undefined}
                aria-describedby={id('exp-hint')} />
              <p className={show('expiration') ? form.error : form.hint} id={id('exp-hint')}>
                {show('expiration') ? 'An exception must end on a date.' : 'Every exception ends. It can be renewed before then.'}
              </p>
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('days')}>Review every <span className={form.optional}>(days, optional)</span></label>
              <input id={id('days')} className={form.input} type="number" min={1} max={EXC_REVIEW_DAYS_MAX} step={1}
                value={f.reviewIntervalDays} onChange={(e) => set('reviewIntervalDays', e.target.value)} />
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('renew')}>Renewals allowed <span className={form.optional}>(optional)</span></label>
              <input id={id('renew')} className={form.input} type="number" min={0} max={EXC_RENEWALS_MAX} step={1}
                value={f.maxRenewals} onChange={(e) => set('maxRenewals', e.target.value)} placeholder="No limit" />
            </div>
          </div>

          <div className={styles.actions}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={pending || ref.isLoading}>
              {pending ? 'Saving…' : record ? 'Save changes' : 'Create exception'}
            </button>
            <Link to={backTo} className={styles.btn}>Cancel</Link>
          </div>
        </form>
      </div>
    </>
  );
}
